import Text from 'components/_UI/Text';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import React from 'react';
import { Phone, Mail, FileText, ArrowUpRight } from 'lucide-react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import paths from 'routes/paths';
import { panelAccent } from 'theme/panelAccent';
import { buildSocialLinks } from 'theme/producerSocialIcons';

function formatPhone(number) {
  if (!number) return '';
  const cleaned = number.replace(/\D/g, '');

  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }

  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }

  return number;
}

function formatCpfCnpj(value) {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');

  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  if (cleaned.length === 14) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  return value;
}

export default function CardProducerProfile({ data }) {
  const socials = buildSocialLinks(data?.socialMedia);
  const publicUrl = data?.slug ? `${paths.producer.page}/${data.slug}` : null;

  const contactItems = [
    data?.phone && { Icon: Phone, label: 'Telefone', value: formatPhone(data.phone) },
    data?.email && { Icon: Mail, label: 'E-mail', value: data.email },
    data?.cpf_cnpj && { Icon: FileText, label: 'CPF/CNPJ', value: formatCpfCnpj(data.cpf_cnpj) },
  ].filter(Boolean);

  return (
    <Container>
      <Banner />

      <Header>
        <AvatarWrapper>
          <DynamicProfileImage
            img={data?.photoImage?.path}
            size={'112px'}
            word={data?.name}
          />
        </AvatarWrapper>

        <HeaderMain>
          <NameRow>
            <Text name='subtitle' paddingBottom={0} color={colors.nightDark}>
              {data?.name}
            </Text>

            {publicUrl && (
              <PublicLink href={publicUrl} target='_blank' rel='noopener noreferrer'>
                Ver perfil público
                <ArrowUpRight size={14} />
              </PublicLink>
            )}
          </NameRow>

          {data?.description && (
            <Text color={colors.night} marginTop='4px'>
              {data.description}
            </Text>
          )}
        </HeaderMain>
      </Header>

      <Body>
        {socials.length > 0 ? (
          <Section>
            <SectionTitle>Redes sociais</SectionTitle>
            <SocialLinksRow>
              {socials.map(social => (
                <SocialLinkButton
                  key={social.key}
                  href={social.href}
                  target='_blank'
                  rel='noopener noreferrer'
                  title={social.label}
                >
                  <social.Icon size={16} />
                  <span>{social.label}</span>
                </SocialLinkButton>
              ))}
            </SocialLinksRow>
          </Section>
        ) : (
          <EmptySocialHint>
            Você ainda não adicionou redes sociais. Clique em "Editar informações" para que apareçam no seu perfil público.
          </EmptySocialHint>
        )}

        {contactItems.length > 0 && (
          <Section>
            <SectionTitle>Contato</SectionTitle>
            <ContactGrid>
              {contactItems.map(({ Icon, label, value }) => (
                <ContactItem key={label}>
                  <ContactIcon>
                    <Icon size={16} color={panelAccent(colors.occasionalPurple)} />
                  </ContactIcon>
                  <ContactText>
                    <ContactLabel>{label}</ContactLabel>
                    <ContactValue>{value}</ContactValue>
                  </ContactText>
                </ContactItem>
              ))}
            </ContactGrid>
          </Section>
        )}
      </Body>
    </Container>
  );
}

const Container = styled(Column)`
  background: ${colors.neutralLight};
  border-radius: 14px;
  width: 100%;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const Banner = styled.div`
  width: 100%;
  height: 72px;
  background: linear-gradient(120deg, ${colors.occasionalPurple}, ${colors.primaryLight});

  @media screen and (min-width: ${breakpoints.md}) {
    height: 96px;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 0 20px 20px;

  @media screen and (min-width: ${breakpoints.md}) {
    padding: 0 28px 20px;
  }
`;

const AvatarWrapper = styled.div`
  position: absolute;
  top: -56px;
  left: 50%;
  transform: translateX(-50%);
  width: 112px;
  height: 112px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid ${colors.neutralLight};
  background: ${colors.neutralLight};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);

  & > picture > img {
    object-fit: cover;
  }

  @media screen and (min-width: ${breakpoints.md}) {
    left: 28px;
    transform: none;
  }
`;

const HeaderMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  text-align: center;
  padding-top: 72px;

  @media screen and (min-width: ${breakpoints.md}) {
    text-align: left;
    padding-top: 16px;
    padding-bottom: 6px;
    margin-left: 132px;
  }
`;

const NameRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  @media screen and (min-width: ${breakpoints.md}) {
    flex-direction: row;
    align-items: center;
  }
`;

const PublicLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${fonts.sizes.small};
  font-weight: ${fonts.weight.bold};
  color: ${panelAccent(colors.occasionalPurple)};
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Body = styled(Column)`
  width: 100%;
  padding: 0 20px 24px;
  gap: 20px;

  @media screen and (min-width: ${breakpoints.md}) {
    padding: 0 28px 28px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionTitle = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${colors.nightLight};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const EmptySocialHint = styled.p`
  margin: 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${colors.neutralBackground};
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
`;

const SocialLinksRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SocialLinkButton = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 20px;
  background: ${colors.neutralBackground};
  border: 1px solid ${colors.neutralDark};
  color: ${colors.nightDark};
  font-size: ${fonts.sizes.small};
  font-weight: 600;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.occasionalPurple}15;
    border-color: ${colors.occasionalPurple};
    color: ${colors.occasionalPurple};
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;

  @media screen and (min-width: ${breakpoints.sm}) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${colors.neutralBackground};
`;

const ContactIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${colors.occasionalPurple}12;
`;

const ContactText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ContactLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: ${colors.nightLight};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const ContactValue = styled.span`
  font-size: 14px;
  color: ${colors.nightDark};
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
